import { EHeadingStyleVariant, ETextStyleVariant, Heading, Text } from '@outdoorsyco/bonfire';
import { FormattedMessage, useIntl } from 'react-intl';

import DestinationIcon from '@/assets/escapes/icons/destination.png';
import RelaxIcon from '@/assets/escapes/icons/relax.png';
import RvIcon from '@/assets/escapes/icons/rv.png';
import { YoutubeVideo } from '@/components/common/YoutubeVideo';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';

type TStep = {
  icon: string;
  title: string;
  description: string;
};

export const HowItWorks = () => {
  const intl = useIntl();

  const steps: TStep[] = [
    {
      icon: DestinationIcon.src,
      title: intl.formatMessage({ defaultMessage: 'Pick your destination', id: 'l36qc0' }),
      description: intl.formatMessage({
        defaultMessage:
          'Choose from handpicked locations with resort-style amenities and scenic views.',
        id: '8mds7f',
      }),
    },
    {
      icon: RvIcon.src,
      title: intl.formatMessage({ defaultMessage: 'Select your RV', id: 'HVDHPa' }),
      description: intl.formatMessage({
        defaultMessage:
          'Stay in a spacious, fully equipped rig with beds, showers, and modern comforts.',
        id: 'Dwd/1G',
      }),
    },
    {
      icon: RelaxIcon.src,
      title: intl.formatMessage({ defaultMessage: 'Arrive & relax', id: '3hjvCr' }),
      description: intl.formatMessage({
        defaultMessage:
          'We deliver and set up your RV—camp chairs out, awning up, ready when you arrive.',
        id: 'XXL+LD',
      }),
    },
  ];

  return (
    <div>
      <Heading
        variant={EHeadingStyleVariant.H3}
        className="mb-3 font-normal text-center text-850 md:text-900 md:mb-4 leading-[35px] md:text-left">
        <FormattedMessage defaultMessage="One simple booking. Endless ways to relax." id="Khbf9Y" />
      </Heading>
      <Text
        variant={ETextStyleVariant.LargeRegular}
        className="text-center md:w-[60%] md:text-left">
        <FormattedMessage
          defaultMessage="Outdoorsy Escapes makes it easy to book a premium RV and a top-rated RV resort in one seamless checkout. No hauling. No hassle. Just show up and settle in."
          id="CZPHR9"
        />
      </Text>
      <div className="flex flex-col items-center lg:px-16 gap-6 lg:flex-row">
        <div className="flex flex-col mt-8 md:mt-10 gap-6 md:gap-4 lg:w-[70%]">
          {steps.map((step, index) => (
            <div
              className="flex flex-col items-center flex-1 gap-3 md:gap-4 md:items-start lg:max-w-[80%]"
              key={index}>
              <div className="mt-1 text-[20px] md:text-[24px]">
                <ResponsiveImage
                  src={step.icon}
                  alt={step.title}
                  height={84}
                  className="h-[64px] md:h-[84px]"
                />
              </div>
              <div className="text-center md:text-left">
                <Text variant={ETextStyleVariant.LargeBold} className="text-lg md:text-xl">
                  {step.title}
                </Text>
                <Text variant={ETextStyleVariant.LargeRegular} className="text-base md:text-lg">
                  {step.description}
                </Text>
              </div>
            </div>
          ))}
        </div>
        <YoutubeVideo
          src="https://www.youtube.com/embed/_RmTM1KRxAY"
          title={intl.formatMessage({
            defaultMessage: 'Introducing Outdoorsy Escapes',
            id: 'h4UloY',
          })}
          className="mt-6 lg:mt-0 lg:w-[60%]"
        />
      </div>
    </div>
  );
};
